import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ApiHealthService from '../../api/health';

const DevToolsContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #2f3941;
  color: white;
  padding: 16px;
  border-radius: 8px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  max-width: 300px;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    display: none;
  }
`;

const StatusIndicator = styled.div<{ status: 'connected' | 'disconnected' | 'testing' }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => 
    props.status === 'connected' ? '#00a86b' :
    props.status === 'disconnected' ? '#cc3340' : '#ffa500'
  };
`;

const StatusText = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;

const DetailText = styled.div`
  font-size: 11px;
  color: #68737d;
  margin-bottom: 4px;
`;

const ToggleButton = styled.button`
  background: none;
  border: 1px solid #68737d;
  color: #68737d;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  margin-top: 8px;
  
  &:hover {
    border-color: white;
    color: white;
  }
`;

const TestButton = styled.button`
  background: #8d4f1b;
  border: none;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  margin-left: 8px;
  
  &:hover {
    background: #703815;
  }
  
  &:disabled {
    background: #68737d;
    cursor: not-allowed;
  }
`;

interface ApiStatus {
  isConnected: boolean;
  responseTime: number;
  error?: string;
  apiUrl: string;
}

const DevelopmentTools: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [apiStatus, setApiStatus] = useState<ApiStatus | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const testApiConnection = async () => {
    setIsTesting(true);
    try {
      const status = await ApiHealthService.testConnection();
      setApiStatus(status);
    } catch (error) {
      setApiStatus({
        isConnected: false,
        responseTime: 0,
        error: 'Test failed',
        apiUrl: process.env.REACT_APP_API_URL || 'Unknown',
      });
    }
    setIsTesting(false);
  };

  useEffect(() => {
    // Test connection on mount
    testApiConnection();
    
    // Test connection every 30 seconds
    const interval = setInterval(testApiConnection, 30000);
    return () => clearInterval(interval);
  }, []);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  if (!isVisible) {
    return (
      <DevToolsContainer style={{ padding: '8px' }}>
        <ToggleButton onClick={() => setIsVisible(true)}>
          Show Dev Tools
        </ToggleButton>
      </DevToolsContainer>
    );
  }

  const getStatusText = () => {
    if (isTesting) return 'Testing...';
    if (!apiStatus) return 'Unknown';
    return apiStatus.isConnected ? 'Connected' : 'Disconnected';
  };

  const getStatusType = (): 'connected' | 'disconnected' | 'testing' => {
    if (isTesting) return 'testing';
    if (!apiStatus) return 'disconnected';
    return apiStatus.isConnected ? 'connected' : 'disconnected';
  };

  return (
    <DevToolsContainer>
      <StatusText>
        <StatusIndicator status={getStatusType()} />
        API Status: {getStatusText()}
        <TestButton onClick={testApiConnection} disabled={isTesting}>
          Test
        </TestButton>
      </StatusText>
      
      {isExpanded && apiStatus && (
        <>
          <DetailText>URL: {apiStatus.apiUrl}</DetailText>
          <DetailText>Response Time: {apiStatus.responseTime}ms</DetailText>
          {apiStatus.error && <DetailText>Error: {apiStatus.error}</DetailText>}
          <DetailText>Mock Data: {process.env.REACT_APP_ENABLE_MOCK_DATA}</DetailText>
        </>
      )}
      
      <div>
        <ToggleButton onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Less' : 'More'}
        </ToggleButton>
        <ToggleButton onClick={() => setIsVisible(false)} style={{ marginLeft: 8 }}>
          Hide
        </ToggleButton>
      </div>
    </DevToolsContainer>
  );
};

export default DevelopmentTools;
